import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import "./tabBar.css";
import IconDots from "../../icons/icon_dots";

/**
 * Drop-in replacement for a hand-rolled ".tabs" row of ".tab" divs, that
 * additionally handles overflow: it shows as many tabs as fit the width of
 * the container they're in, and folds whichever don't fit behind a "..."
 * button at the end that opens a dropdown listing them, instead of wrapping
 * or clipping. Underneath the row it renders a full-width title bar naming
 * the selected tab - handy on its own, and especially so when the selected
 * tab is one of the ones folded behind "...". The title bar's background
 * matches the content panel each page renders below it, so the two read as
 * one continuous surface.
 *
 * `tabs` is an array of {key, label} - label can be any ReactNode (plain
 * text, or text plus an icon). `theme` picks which of the app's two tab
 * color schemes to use ("dark" or "purple" - see tabBar.css); defaults to
 * "dark".
 */
export default ({tabs, selected, onSelect, theme = "dark"}) => {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const dotsMeasureRef = useRef(null);
    const rowRef = useRef(null);
    const dropdownRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(tabs.length);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownRect, setDropdownRect] = useState(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        const dotsMeasure = dotsMeasureRef.current;
        if (!container || !measure || !dotsMeasure) return;

        const recalc = () => {
            const available = container.clientWidth;
            const widths = Array.from(measure.children).map((el) => el.getBoundingClientRect().width);
            const totalWidth = widths.reduce((sum, w) => sum + w, 0);

            if (totalWidth <= available) {
                setVisibleCount(tabs.length);
                return;
            }

            // Some tabs won't fit - reserve room for the "..." button and
            // fit as many tabs as possible, in order, ahead of it.
            const budget = available - dotsMeasure.getBoundingClientRect().width;
            let sum = 0;
            let count = 0;
            for (const width of widths) {
                if (sum + width > budget) break;
                sum += width;
                count++;
            }
            setVisibleCount(count);
        };

        recalc();

        const observer = new ResizeObserver(recalc);
        observer.observe(container);
        return () => observer.disconnect();
    }, [tabs]);

    const openDropdown = () => {
        // Positioned in the viewport and rendered through a portal, so it
        // isn't clipped by a scrolling ancestor like
        // ".tabs-content { overflow-y: auto }".
        const rect = rowRef.current.getBoundingClientRect();
        setDropdownRect({top: rect.bottom, right: window.innerWidth - rect.right, minWidth: 0});
        setDropdownOpen(true);
    };

    useEffect(() => {
        if (!dropdownOpen) return;

        const closeIfOutside = (e) => {
            if (
                (!dropdownRef.current || !dropdownRef.current.contains(e.target)) &&
                (!rowRef.current || !rowRef.current.contains(e.target))
            ) {
                setDropdownOpen(false);
            }
        };
        // A scroll anywhere (this row's own scroll container included) or a
        // resize invalidates the position computed above - simplest to just
        // close rather than track and recompute it continuously.
        const closeUnconditionally = () => setDropdownOpen(false);

        document.addEventListener("mousedown", closeIfOutside);
        window.addEventListener("scroll", closeUnconditionally, true);
        window.addEventListener("resize", closeUnconditionally);
        return () => {
            document.removeEventListener("mousedown", closeIfOutside);
            window.removeEventListener("scroll", closeUnconditionally, true);
            window.removeEventListener("resize", closeUnconditionally);
        };
    }, [dropdownOpen]);

    const visibleTabs = tabs.slice(0, visibleCount);
    const hiddenTabs = tabs.slice(visibleCount);
    const hiddenSelected = hiddenTabs.some((tab) => tab.key === selected);
    const selectedTab = tabs.find((tab) => tab.key === selected);

    return (
        <div className={"tabbar"} data-theme={theme} ref={containerRef}>
            {/* Off-screen copy of the full tab row, used only to measure
                each tab's natural width so it's known up front how many fit,
                before the real (visible) row is painted. */}
            <div className={"tabs tabbar-measure"} ref={measureRef} aria-hidden={"true"}>
                {
                    tabs.map((tab) => (
                        <div key={tab.key} className={"tab"}>{tab.label}</div>
                    ))
                }
            </div>
            <div className={"tabs tabbar-measure"} aria-hidden={"true"}>
                <div className={"tab tabbar-dots"} ref={dotsMeasureRef}>
                    <IconDots/>
                </div>
            </div>
            <div className={"tabs"} ref={rowRef}>
                {
                    visibleTabs.map((tab) => (
                        <div
                            key={tab.key}
                            className={"tab" + (tab.key === selected ? " selected" : "")}
                            onClick={() => onSelect(tab.key)}
                        >
                            {tab.label}
                        </div>
                    ))
                }
                {
                    hiddenTabs.length > 0 &&
                    <div
                        className={"tab tabbar-dots" + (hiddenSelected || dropdownOpen ? " selected" : "")}
                        onClick={() => (dropdownOpen ? setDropdownOpen(false) : openDropdown())}
                    >
                        <IconDots/>
                    </div>
                }
            </div>
            <div className={"tabbar-title"}>
                <div className={"underline"}>{selectedTab?.label}</div>
            </div>
            {
                dropdownOpen && dropdownRect && createPortal(
                    <div
                        className={"tabbar tabbar-dropdown-portal"}
                        data-theme={theme}
                        ref={dropdownRef}
                        style={{top: dropdownRect.top, right: dropdownRect.right, minWidth: dropdownRect.minWidth}}
                    >
                        <div className={"tabs tabbar-dropdown"}>
                            {
                                hiddenTabs.map((tab) => (
                                    <div
                                        key={tab.key}
                                        className={"tab" + (tab.key === selected ? " selected" : "")}
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            onSelect(tab.key);
                                        }}
                                    >
                                        {tab.label}
                                    </div>
                                ))
                            }
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    )
}
